/**
 * First visit: no forms. The 3D belly is right there; she taps where baby moved,
 * can try "จด", and sees a small report of what she just did. Only when she
 * taps "ใช้ต่อ" do we ask for consent, due date and names (then home).
 * Everything stays on this device until she makes an account.
 */
import { repo } from '../../data/repo';
import { track } from '../../data/track';
import { hourHistogram, ZONES, zoneCounts } from '../../domain/stats';
import { makeEntry, TAGS } from '../../domain/journal';
import { newId } from '../../data/localStore';
import { t } from '../../i18n';
import { sfx } from '../../audio/sfx';
import { ambient } from '../../audio/ambient';
import { $, $$, esc, openSheet, vibrate } from '../dom';
import { go, register, type Screen } from '../router';
import { mountBelly, unmountBelly } from '../bellyHost';
import { openJournal } from '../journalSheet';

const QUICK_TAGS = ['after_meal', 'music', 'voice', 'resting'] as const;
let skippedNote = false;
let shownStep = 0;

function counterText(): string {
  const n = repo.kicks.length;
  return n ? t('start', 'count', { n }) : t('start', 'hint');
}

/** The guide under the belly: 1 tap → 2 one-tap note → 3 see the report. Nothing ever covers the belly. */
function currentStep(): 1 | 2 | 3 {
  if (!repo.kicks.length) return 1;
  if (!repo.journal.length && !skippedNote) return 2;
  return 3;
}

function stepHtml(step: 1 | 2 | 3): string {
  const badge = `<span class="step-n">${step}</span>`;
  if (step === 1) return `<div class="step-card" data-step="1">${badge}<div><b>${esc(t('start', 'step1'))}</b><p class="small muted">${esc(t('start', 'step1Sub'))}</p></div></div>`;
  if (step === 2) return `<div class="step-card" data-step="2">${badge}<div style="flex:1;min-width:0"><b>${esc(t('start', 'step2'))}</b>
      <div class="quick-tags">${QUICK_TAGS.map(id => `<button class="chip tag-chip" data-action="quick-tag" data-tag="${id}"><span class="tag-emoji">${TAGS.find(x => x.id === id)!.emoji}</span>${esc(t('tags', id))}</button>`).join('')}</div>
      <div class="row" style="justify-content:space-between;margin-top:6px"><button class="link-btn small" data-action="start-jot">${esc(t('start', 'step2More'))}</button><button class="link-btn small muted" data-action="skip-note">${esc(t('start', 'step2Skip'))}</button></div></div></div>`;
  return `<div class="step-card" data-step="3">${badge}<div style="flex:1"><b>${esc(t('start', 'step3'))}</b>
      <button class="btn btn-primary btn-block" style="margin-top:8px" data-action="start-report">${esc(t('start', 'step3Btn'))}</button></div></div>`;
}

/** Second visit onwards (she has seen the guide once): a plain row of actions, no walkthrough. */
// Decided once per page load: closing the tab without leaving the screen still counts as "seen".
let tourThisVisit: boolean | null = null;
const returning = () => {
  if (tourThisVisit === null) tourThisVisit = !repo.ui.startTourDone;
  return !tourThisVisit;
};

function actionsHtml(): string {
  const none = repo.kicks.length === 0 ? 'disabled' : '';
  return `<div class="start-actions">
      <button class="btn btn-ghost" data-action="start-jot">${esc(t('journal', 'button'))}</button>
      <button class="btn btn-ghost" data-action="start-report" ${none}>${esc(t('start', 'seeReport'))}</button>
      <button class="btn btn-primary" data-action="start-continue" ${none}>${esc(t('start', 'continue'))}</button>
    </div>`;
}

function refreshUi(root: ParentNode = document): void {
  const c = $('#start-count', root as HTMLElement); if (c) c.textContent = counterText();
  if (returning()) {
    const box = $('#start-step', root as HTMLElement);
    if (box) box.innerHTML = actionsHtml();
    return;
  }
  const step = currentStep();
  const box = $('#start-step', root as HTMLElement);
  if (box && step !== shownStep) {
    box.innerHTML = stepHtml(step);
    if (shownStep) box.firstElementChild?.classList.add('swap-in');
    shownStep = step;
  }
}

function animateReport(): void {
  // numbers count up, then the zone map and hour bars fill in (CSS handles the rest)
  $$('#sheet-overlay [data-count]').forEach(el => {
    const to = Number(el.dataset.count); const t0 = performance.now();
    const tick = (now: number) => { const p = Math.min(1, (now - t0) / 700); el.textContent = String(Math.round(to * (1 - (1 - p) ** 3))); if (p < 1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  });
}

function reportHtml(): string {
  const kicks = repo.kicks;
  const counts = zoneCounts(kicks, 0);
  const total = ZONES.reduce((a, z) => a + counts[z], 0);
  const maxZ = Math.max(1, ...ZONES.map(z => counts[z]));
  const top = total ? ZONES.slice().sort((a, b) => counts[b] - counts[a])[0]! : null;
  const hist = hourHistogram(kicks, 0);
  const maxH = Math.max(1, ...hist);
  const peak = hist.indexOf(Math.max(...hist));
  const tagCount = new Map<string, number>();
  for (const e of repo.journal) for (const tg of e.tags) tagCount.set(tg, (tagCount.get(tg) ?? 0) + 1);
  const tags = [...tagCount.entries()].sort((a, b) => b[1] - a[1]);
  const notes = repo.journal.filter(e => e.note).length;
  return `
    <h2 style="margin-top:0">${esc(t('start', 'reportTitle'))}</h2>
    <p class="muted small" style="margin:2px 0 14px">${esc(t('start', 'reportLead'))}</p>
    <div class="grid-2">
      <div class="card stat-tile"><div class="v num" style="color:var(--rose-dark)" data-count="${kicks.length}">0</div><div class="k">${esc(t('start', 'rTaps'))}</div></div>
      <div class="card stat-tile"><div class="v num" style="color:var(--amber)" data-count="${repo.journal.length}">0</div><div class="k">${esc(t('start', 'rNotes'))}</div></div>
    </div>
    <div class="card">
      <h3 style="margin-bottom:10px">${esc(t('stats', 'whereTitle'))}</h3>
      <div class="zone-map report-anim">${ZONES.map(z => {
        const a = counts[z] / maxZ;
        return `<div class="zone-cell" style="background:rgba(212,132,124,${(0.1 + a * 0.75).toFixed(2)});color:${a > 0.6 ? '#fff' : 'var(--fg2)'}"><b>${counts[z]}</b>${esc(t('zones', z))}</div>`;
      }).join('')}</div>
      <p class="small muted" style="text-align:center;margin-top:10px">${top && ZONES.filter(z => counts[z] === counts[top]).length > 1 ? esc(t('start', 'rTie')) : top ? esc(t('stats', 'top', { z: t('zones', top), p: Math.round((counts[top] / total) * 100) })) : esc(t('stats', 'noZone'))}</p>
    </div>
    <div class="card">
      <h3 style="margin-bottom:10px">${esc(t('stats', 'hoursTitle'))}</h3>
      <div class="hours report-anim">${hist.map(v => `<i style="height:${Math.max(2, (v / maxH) * 100)}%;${v ? '' : 'background:var(--border)'}"></i>`).join('')}</div>
      <div class="hours-axis"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
      <p class="small muted" style="text-align:center;margin-top:8px">${hist.filter(v => v === Math.max(...hist)).length > 1 ? esc(t('start', 'rTie')) : esc(t('stats', 'peak', { h: `${String(peak).padStart(2, '0')}:00–${String((peak + 1) % 24).padStart(2, '0')}:00` }))}</p>
    </div>
    <div class="card">
      <h3 style="margin-bottom:8px">${esc(t('start', 'rTagsTitle'))}</h3>
      ${tags.length || notes ? `<div class="report-tags">${tags.map(([id, n]) => {
          const tag = TAGS.find(x => x.id === id);
          return `<span class="report-tag">${tag ? `<span class="rt-emoji">${tag.emoji}</span>` : ''}<span>${esc(tag ? t('tags', tag.id) : id)}</span><b class="num">×${n}</b></span>`;
        }).join('')}${notes ? `<span class="report-tag"><span>${esc(t('start', 'rNotesWritten', { n: notes }))}</span></span>` : ''}</div>`
        : `<p class="small muted" style="margin:0">${esc(t('start', 'rTagsEmpty'))}</p>`}
    </div>
    <p class="small muted" style="text-align:center">${esc(t('start', 'reportMore'))}</p>
    <button class="btn btn-primary btn-block" data-action="start-continue" style="margin-top:6px">${esc(t('start', 'continue'))}</button>`;
}

const start: Screen = {
  id: 'start',
  render: () => {
    shownStep = 0;
    return `
    <div class="ob-top" style="justify-content:flex-end"><button class="link-btn small" data-action="go" data-to="login">${esc(t('start', 'haveAccount'))}</button></div>
    <div class="start-head">
      <h2 style="margin-bottom:2px">${esc(returning() ? t('start', 'welcomeBack') : t('welcome', 'titleLocal'))}</h2>
      <p class="muted small" style="margin:0">${esc(returning() ? t('start', 'welcomeBackLead') : t('start', 'lead'))}</p>
    </div>
    <div class="stage-wrap start-stage-wrap" style="flex:1;display:flex;flex-direction:column">
      <div class="belly-stage" id="start-stage"></div>
      <div class="stage-hint" id="start-count">${esc(counterText())}</div>
    </div>
    <div class="ob-footer start-footer">
      <div id="start-step"></div>
      <p class="small muted" style="text-align:center;margin:8px 0 0">${esc(t('start', 'privacyLine'))}</p>
    </div>`;
  },
  mount(root) {
    ambient.want(true);
    if (returning()) track.daily('start_return');                    // fix this visit's layout first
    if (!repo.ui.startTourDone) repo.setUi({ startTourDone: true }); // ...then remember it for next time
    void mountBelly($('#start-stage', root)!, (hit) => {
      repo.addKick({
        occurredAt: Date.now(), source: 'tap', sessionClientId: null,
        zone: hit ? hit.zone : null, x: hit ? hit.x : null, y: hit ? hit.y : null, z: hit ? hit.z : null
      });
      sfx.kick(Math.min(repo.kicks.length, 10)); vibrate(20);
      track.once('first_kick'); track.daily('kick_day');
      refreshUi(root);
    }, { spatial: true, markers: repo.kicks.filter(k => k.x !== null).map(k => ({ x: k.x!, y: k.y!, z: k.z! })) });
    refreshUi(root);
  },
  refresh: () => refreshUi(),
  unmount: () => { unmountBelly(); ambient.want(false); },
  actions: {
    'quick-tag': (el) => {
      const e = makeEntry(newId(), Date.now(), [el.dataset.tag!], '');
      if (!e) return;
      el.classList.add('picked'); vibrate(15); sfx.complete();
      // let her see the chip light up, then move on to the report step
      track.once('first_note');
      setTimeout(() => { repo.setUi({ jotLearned: true }); repo.addJournal(e); }, 450);
    },
    'skip-note': () => { skippedNote = true; refreshUi(); },
    'start-jot': () => { openJournal(); },
    'start-report': () => {
      track.once('first_report');
      openSheet(reportHtml(), { 'start-continue': () => { track.once('first_continue'); go('how'); } }, { className: 'report-sheet' });
      animateReport();
    },
    'start-continue': () => { track.once('first_continue'); go('how'); }
  }
};

register(start);
